import merge from 'lodash.merge';
import { DeepPartial } from './util';

export type TUserPermissions = {
  accessManagement: { admin: boolean };
  adk: {
    changesRequested: boolean;
    createProject: boolean;
    requestProject: boolean;
    reviewProject: boolean;
    admin: boolean;
    codeAccess: Record<string, boolean>;
    onDeviceActivation: Record<string, boolean>;
  };
  android: {
    admin: boolean;
  };
  api: {
    sku: boolean;
    admin: boolean;
  };
  contentProvider: {
    additionalFields: Record<string, boolean>;
    entitlementPushBasedApi: Record<string, boolean>;
    refundsAndChargebacksPushBasedApi: Record<string, boolean>;
  };
  perks: { admin: boolean };
  admin: boolean;
  isDisney: boolean;
  isPartner: boolean;
  services: boolean;
  switchPartner: boolean;
  workstreams: string[];
};

export type TDocsPermissions = {
  workstreams: string[];
  draft: boolean;
  isDisney: boolean;
  isPartner: boolean;
};

export type TMiraPermissions = {
  isDisney: boolean;
  isPartner: boolean;
};

export const initialPermissions: TUserPermissions = {
  accessManagement: { admin: false },
  adk: {
    changesRequested: false,
    createProject: false,
    requestProject: false,
    reviewProject: false,
    admin: false,
    codeAccess: {},
    onDeviceActivation: {},
  },
  android: {
    admin: false,
  },
  api: {
    sku: false,
    admin: false,
  },
  contentProvider: {
    additionalFields: {},
    entitlementPushBasedApi: {},
    refundsAndChargebacksPushBasedApi: {},
  },
  perks: { admin: false },
  admin: false,
  isDisney: false,
  isPartner: false,
  services: false,
  switchPartner: false,
  workstreams: [],
};

export function hasCodeAccess(permissions: TUserPermissions, partnerId: string): boolean {
  return !!permissions.adk.codeAccess['*'] || !!permissions.adk.codeAccess[partnerId];
}

export function hasOnDeviceActivationAccess(permissions: TUserPermissions, partnerId: string): boolean {
  return !!permissions.adk.onDeviceActivation['*'] || !!permissions.adk.onDeviceActivation[partnerId];
}

export function hasContentProviderAdditionalFieldsAccess(permissions: TUserPermissions, partnerId: string): boolean {
  return (
    !!permissions.contentProvider.additionalFields['*'] || !!permissions.contentProvider.additionalFields[partnerId]
  );
}

export function hasEntitlementPushBasedApiAccess(permissions: TUserPermissions, partnerId: string): boolean {
  return (
    !!permissions.contentProvider.entitlementPushBasedApi['*'] ||
    !!permissions.contentProvider.entitlementPushBasedApi[partnerId]
  );
}

export function hasRefundsAndChargebacksPushBasedApiAccess(permissions: TUserPermissions, partnerId: string): boolean {
  return (
    !!permissions.contentProvider.refundsAndChargebacksPushBasedApi['*'] ||
    !!permissions.contentProvider.refundsAndChargebacksPushBasedApi[partnerId]
  );
}

export const addTestPermissions = (newPermissions?: DeepPartial<TUserPermissions>): TUserPermissions => {
  const permissions = merge({}, initialPermissions, newPermissions);
  return permissions;
};

export const addTestMiraPermissions = (newPermissions?: DeepPartial<TMiraPermissions>): TMiraPermissions => {
  const initialMiraPermissions: TMiraPermissions = {
    isDisney: false,
    isPartner: false,
  };
  const permissions = merge({}, initialMiraPermissions, newPermissions);
  return permissions;
};
